import type { ReactNode } from "react";
import { Link, NavLink as RouterNavLink, useNavigate } from "react-router";
import { BookrLogo, BookrMark } from "@/design/BookrLogo";
import { buttonClass } from "@/design/buttons";
import { ThemeToggle } from "@/design/ThemeToggle";
import { roleHome } from "@/modules/identity/surfaces";
import { useMe } from "~/auth/MeProvider";
import { ConsumerChrome } from "./ConsumerChrome";

/**
 * The public header and footer (R22 T12).
 *
 * **The header reflects the session, and now it reads it rather than being
 * rendered with it.** On the Next host this was a server component that
 * resolved the caller itself; here it asks the one `/me` the whole app shares.
 * That is a better shape than it looks: the header used to be a second caller
 * resolution on every page, and a header that 500s is a far worse outcome than
 * one that is briefly plain.
 *
 * **It renders the logged-out chrome while `/me` is still in flight**, and this
 * is the one place in the app where that is right rather than dangerous. The
 * header decides nothing; showing "Log in" for a moment to somebody who turns
 * out to be signed in is a flicker, whereas holding the whole page back for the
 * chrome would make every cold load feel slow. The *guards* wait, because they
 * decide.
 *
 * Honest links only: no WhatsApp, no pricing, no fabricated claims.
 */
export function SiteHeader() {
  const { me, signOut } = useMe();
  const navigate = useNavigate();

  // A signed-in Consumer gets the app navigation; everybody else gets the
  // marketing header, which deliberately carries no section links. Since H28
  // the guest surfaces are exactly where a signed-in customer spends their
  // time, so handing them a "Sign up" button here would be absurd.
  if (me?.kind === "consumer") return <ConsumerChrome />;

  const signedIn = me && me.kind !== "guest";

  return (
    <header className="sticky top-0 z-40 border-b border-line bg-surface/95 backdrop-blur">
      <div className="mx-auto flex h-16 w-full max-w-6xl items-center justify-between px-4 sm:px-5">
        <Link to="/" aria-label="Bookr home">
          <BookrLogo size={26} />
        </Link>
        <nav className="flex items-center gap-2 text-sm font-medium sm:gap-4">
          {!signedIn ? (
            <>
              <NavLink to="/login">Log in</NavLink>
              <Link to="/signup" className={buttonClass("primary", "px-4 py-2")}>
                Sign up
              </Link>
            </>
          ) : (
            <>
              <NavLink to={roleHome(me.kind)} className="font-semibold text-ink">
                Dashboard
              </NavLink>
              <button
                type="button"
                className="text-sm font-medium text-muted hover:text-ink"
                onClick={() => {
                  void signOut().then(() => navigate("/", { replace: true }));
                }}
              >
                Log out
              </button>
            </>
          )}
          <ThemeToggle />
        </nav>
      </div>
    </header>
  );
}

/** A header link that knows whether it is the current screen. */
export function NavLink({
  to,
  children,
  className,
}: {
  to: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <RouterNavLink
      to={to}
      className={({ isActive }) =>
        `rounded-lg px-2 py-1.5 transition-colors ${
          isActive ? "text-ink" : "text-muted hover:text-body"
        } ${className ?? ""}`
      }
    >
      {children}
    </RouterNavLink>
  );
}

/**
 * The public footer. Support and Privacy are both App Store requirements for
 * the mobile app and both have to be reachable from the site rather than only
 * by direct URL: a reviewer looks for them here.
 */
export function SiteFooter() {
  return (
    <footer className="border-t border-line bg-page">
      <div className="mx-auto flex w-full max-w-5xl flex-col items-center justify-between gap-2 px-4 py-8 text-sm text-muted sm:flex-row">
        <span className="flex items-center gap-2">
          <BookrMark size={20} />© 2026 Bookr LB
        </span>
        <nav className="flex gap-4">
          <Link to="/search" className="transition-colors hover:text-body">
            Find care
          </Link>
          <Link to="/join" className="transition-colors hover:text-body">
            For businesses
          </Link>
          <Link to="/login" className="transition-colors hover:text-body">
            Log in
          </Link>
          <Link to="/support" className="transition-colors hover:text-body">
            Support
          </Link>
          <Link to="/privacy" className="transition-colors hover:text-body">
            Privacy
          </Link>
        </nav>
      </div>
    </footer>
  );
}

/** Header, page, footer: the frame every public screen sits in. */
export function PublicPage({ children }: { children: ReactNode }) {
  return (
    <main className="flex min-h-screen grow flex-col bg-page text-body">
      <SiteHeader />
      {children}
      <SiteFooter />
    </main>
  );
}
